import React, { useEffect, useState, useCallback, ReactNode, MouseEvent, HTMLAttributes, useRef, useMemo } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/CircleRounded';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material';

const Sublist = styled('ul')(() => ({
  margin: 0,
  listStyleType: 'none',
  padding: '0 0 10px 24px',
}));

export interface NodeSelectionChange<N> {
  node: N;
  value: boolean | null;
};

type OnChangesCallback<N> = (changes: NodeSelectionChange<N>[]) => void;

type CommonProps<N> = {
  idGetter: (n: N) => string | number;
  nameGetter: (n: N) => ReactNode;
  hasCheckboxGetter?: (n: N) => boolean;
  childrenGetter: (n: N) => N[] | null;
  onChange?: (node: N, value: boolean | null) => void;
  onToggle?: (node: N, value: boolean) => void;
  initialStateGetter?: (n: N) => boolean;
  isActiveGetter?: (n: N) => boolean;
  checkboxIconGetter?: (n: N, checked: boolean | null) => ReactNode;
  iconGetter?: (n: N) => ReactNode;
  onChanges?: OnChangesCallback<N>;
  checkMode?: 'node' | 'toggler';
}

type TreeNodeProperties<N> = CommonProps<N> & {
  node: N,
  level: number,
  style?: React.CSSProperties;
  selection: Map<unknown, boolean | null>;
  onChangeBubble?: (changes?: NodeSelectionChange<N>[]) => void;
}

type CheckTreeViewProps<N> = CommonProps<N> & {
  roots: N[];
  style?: React.CSSProperties;
  initialValueGetter?: (n: N) => boolean | null;
};

const Li = styled('li')({
  whiteSpace: 'nowrap',
  marginLeft: '0px',
});

const AlignedDiv = styled('div')({
  verticalAlign: 'middle',
  height: '24px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: 'fit-content',
});

function NodeTitle({ children, className, level, isActive, ...props }: HTMLAttributes<HTMLDivElement> & { isActive: boolean; level: number; }): ReactNode {
  const cn = `node level${level}${isActive ? '' : ' disabled'}` + (className ? ` ${className}` : '');
  return <AlignedDiv className={cn} {...props}>{ children }</AlignedDiv>
}

const CollapsedIcon = styled(ChevronRightIcon)({
  fontSize: '24px',
});

const ExpandedIcon = styled(ExpandMoreIcon)({
  fontSize: '24px',
});

function ExpandButton({ expanded, ...props}: SvgIconProps & { expanded: boolean }): ReactNode {
  return expanded
    ? (<ExpandedIcon className='expanded' {...props}/>)
    : (<CollapsedIcon className='collapsed' {...props}/>)
}

function setToBranch<N>(
  node: N,
  v: boolean,
  selection: Map<unknown, boolean | null>,
  props: Pick<CheckTreeViewProps<N>, 'childrenGetter' | 'hasCheckboxGetter' | 'onChange' | 'idGetter' >,
  changes?: NodeSelectionChange<N>[], 
): void {
  const { childrenGetter, hasCheckboxGetter, onChange, idGetter } = props;

  const nodes = childrenGetter(node);
  if (nodes) {
    const checkableSubs = nodes.filter((n) => (hasCheckboxGetter ? hasCheckboxGetter(n) : false));
    for (const n of checkableSubs) {
      setToBranch(n, v, selection, props, changes);
    }
  }

  selection.set(idGetter(node), v);

  if (onChange) {
    onChange(node, v);
  }
  if (changes) {
    changes.push({ node, value: v });
  }
}

function TreeNode<N>({
  node, idGetter, nameGetter, iconGetter, hasCheckboxGetter, childrenGetter,
  onChange, onToggle, initialStateGetter,
  isActiveGetter, checkboxIconGetter, level, selection,
  onChangeBubble, onChanges, checkMode = 'node'
}: TreeNodeProperties<N>): ReactNode {
  const id = idGetter(node);
  const name = nameGetter(node);
  const nodes = childrenGetter(node);
  const hasSubs = Array.isArray(nodes) && nodes.length;
  const hasCheckbox = hasCheckboxGetter ? hasCheckboxGetter(node) : false;
  const isActive = isActiveGetter ? isActiveGetter(node) : true;
  const checked = selection.get(id);

  const [changeCounter, setChangeCounter] = useState<number>(0);

  const update = useCallback(() => {
    setChangeCounter(changeCounter < Number.MAX_SAFE_INTEGER ? changeCounter + 1 : 0);
  }, [changeCounter])

  const [expanded, setExpanded] = useState((initialStateGetter && initialStateGetter(node)) || false);

  const change = useCallback((e: unknown, v: boolean) => {
    selection.set(id, v);
    const changes = onChanges ? [] as NodeSelectionChange<N>[] : undefined;
    setToBranch(node, v, selection, { idGetter, childrenGetter, hasCheckboxGetter, onChange }, changes);
    if (onChangeBubble) onChangeBubble(changes);
    if (onChanges && changes) onChanges(changes);
    update();
  }, [ id, node, onChange, childrenGetter, hasCheckboxGetter, onChange, onChangeBubble, onChanges, selection, update ]);

  const toggle = useCallback(() => {
    if (onToggle) onToggle(node, !expanded);
    setExpanded(!expanded);
  }, [expanded, node]);

  const onCheckBubble = useCallback((changes?: NodeSelectionChange<N>[]) => {
    if (nodes) {
      const checkableSubs = nodes.filter((n) => (hasCheckboxGetter ? hasCheckboxGetter(n) : false));
      const stats = checkableSubs.reduce((acc, n) => {
        const v = selection.get(idGetter(n));
        if (v === true) acc.checked++;
        if (v === false) acc.unchecked++;
        return acc;
      }, { checked: 0, unchecked: 0 });

      const value = checkableSubs.length === stats.checked
        ? true
        : ( checkableSubs.length === stats.unchecked ? false : null );

      if (onChange) {
        onChange(node, value);
      }

      if (changes) {
        changes.push({ node, value });
      }

      selection.set(id, value);

      if (onChangeBubble) onChangeBubble(changes);

      update();
    }
  }, [id, node, hasCheckboxGetter, nodes, onChange, onChangeBubble, selection, update])

  const click = useCallback((e: MouseEvent) => {
    if (hasSubs) {
      toggle();
    } else if (hasCheckbox && checkMode === 'node') {
      e.stopPropagation();
      e.preventDefault();
      change(null, !checked);
    }
  }, [change, checked, hasCheckbox, hasSubs, toggle, node]);

  return (
    <Li key={String(id)}>
      <NodeTitle onClick={isActive ? click : undefined} isActive={isActive} level={level}>
        {
          hasSubs ? <ExpandButton expanded={expanded} onClick={toggle}/> : null
        }
        {hasCheckbox &&
          <Checkbox
            checked={!!checked}
            indeterminate={checked === null}
            icon={checkboxIconGetter && checkboxIconGetter(node, false)}
            checkedIcon={checkboxIconGetter && checkboxIconGetter(node, true)}
            indeterminateIcon={checkboxIconGetter ? checkboxIconGetter(node, null) : <IndeterminateCheckBoxOutlinedIcon/>}
            disabled={!isActive}
            onClick={(e) => e.stopPropagation()}
            onChange={change}
            size="small"
            style={{ padding: 0, margin: 0 }}
          />
        }
        {iconGetter && iconGetter(node)}
        {name}
      </NodeTitle>
      {nodes && expanded
        ? (
        <Sublist>{
          nodes.map((node, idx) =>
            <TreeNode
              key={`${id}_${idx}`}
              node={node}
              level={level + 1}
              idGetter={idGetter}
              nameGetter={nameGetter}
              checkboxIconGetter={checkboxIconGetter}
              iconGetter={iconGetter}
              hasCheckboxGetter={hasCheckboxGetter}
              childrenGetter={childrenGetter}
              onChange={onChange}
              onToggle={onToggle}
              initialStateGetter={initialStateGetter}
              isActiveGetter={isActiveGetter}
              selection={selection}
              onChangeBubble={onCheckBubble}
              onChanges={onChanges}
              checkMode={checkMode}
            />)
        }</Sublist>)
        : null}
    </Li>
  );
}

function collectValue<N>(
  node: N,
  mutableSelected: Map<unknown, boolean | null>,
  props: Pick<CheckTreeViewProps<N>, 'childrenGetter' | 'hasCheckboxGetter' | 'initialValueGetter' | 'idGetter'>,
): void {
  const { childrenGetter, hasCheckboxGetter, initialValueGetter, idGetter } = props;
  const id = idGetter(node);
  const nodes = childrenGetter(node);
  if (nodes) {
    const checkableSubs = nodes.filter((n) => (hasCheckboxGetter ? hasCheckboxGetter(n) : false));
    const checked = checkableSubs.reduce(
      (s: number, sn: N) => {
        collectValue(sn, mutableSelected, props);
        const snId = idGetter(sn);
        return s + Number(mutableSelected.get(snId) || 0)
      },
      0
    );
    const value = !checked
      ? false
      : (checkableSubs.length === checked
        ? true
        : null
      );
    mutableSelected.set(id, value);
    return;
  }
  mutableSelected.set(id, initialValueGetter ? initialValueGetter(node) : false);
}

export default function CheckTreeView<N>({ roots, style, ...treeProps }: CheckTreeViewProps<N>): ReactNode {

  const { idGetter, initialValueGetter, ...restNodeProps } = treeProps;

  const selection = useRef<Map<unknown, boolean | null>>(new Map());
  const uniquenessCheckerMap = useRef<Map<unknown, N>>(new Map());

  uniquenessCheckerMap.current.clear();

  const wrappedIdGetter = useCallback((n: N): string | number => {
    const id = idGetter(n);
    const prev = uniquenessCheckerMap.current.get(id);
    if (!prev) {
      uniquenessCheckerMap.current.set(id, n);
    } else if (prev !== n) {
      throw new Error(`Non unique tree node id detected: ${String(id)}`);
    }
    return id;
  }, [ idGetter ]);

  useMemo(() => {
    selection.current.clear();
    for (const r of roots) {
      collectValue(r, selection.current, treeProps);
    }
  }, [ roots ]);

  return (
    <ul className="bimdata-checked-tree" style={{ ...style, marginLeft: 0, listStyleType: 'none' }}>
      { roots.map((node) => (
        <TreeNode<N>
          key={String(wrappedIdGetter(node))}
          node={node}
          {...restNodeProps}
          level={0}
          selection={selection.current}
          idGetter={wrappedIdGetter}
        />
      )) }
    </ul>
  );
}